import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Shield, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import LanguageSelector from '../components/LanguageSelector';
import SeoMeta from '../components/SeoMeta';

export default function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success(t('common.success'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(t('auth.login.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex">
      <SeoMeta title="Connexion" description="Connectez-vous à votre compte Drone Maps." noindex />
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors">
              <ArrowLeft className="w-4 h-4" />
              {t('common.back')}
            </Link>
            <LanguageSelector variant="compact" />
          </div>

          <Link to="/" className="inline-flex items-center mb-8" data-testid="login-logo">
            <img src="/logo-landscape.png" alt="Drone Maps" className="h-12 w-auto" />
          </Link>

          <h1 className="text-3xl font-bold text-white font-[Barlow] mb-2">{t('auth.login.title')}</h1>
          <p className="text-slate-400 mb-8">{t('auth.login.subtitle')}</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-slate-300">{t('auth.login.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="mt-2 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:ring-sky-500 focus:border-sky-500"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-300">{t('auth.login.password')}</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:ring-sky-500 focus:border-sky-500 pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-sky-500 hover:bg-sky-600 text-white font-semibold"
              data-testid="login-submit-btn"
            >
              {loading ? t('common.loading') : t('auth.login.submit')}
            </Button>
          </form>

          <p className="mt-8 text-center text-slate-400 text-sm">
            {t('auth.login.noAccount')}{' '}
            <Link to="/register" className="text-sky-400 hover:text-sky-300 font-medium">
              {t('auth.login.createAccount')}
            </Link>
          </p>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/flagged/photo-1557316400-043729e3455e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxhZXJpYWwlMjB2aWV3JTIwZnJhbmNlJTIwbGFuZHNjYXBlJTIwZmllbGRzJTIwY2l0eXxlbnwwfHx8fDE3NzEyNDgzMTd8MA&ixlib=rb-4.1.0&q=85)'
          }}
        />
        <div className="absolute inset-0 bg-slate-900/60" />
      </div>
    </div>
  );
}
